import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { Form } from "@/components/ReactHookForm/form";
import { Button } from "@/components/ui/button";
import { CheckboxControl, TextControl } from "@/components/ReactHookForm/formInputs";
import { useApi } from "@/hooks/useApi";
import { toast } from "react-toastify";

export function RegisterForm() {
	const navigate = useNavigate();
	const api = useApi();
	const mutation = api.useMutation("post", "/v1/Auth/register");
	const form = useForm<FormType>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			razonSocial: "",
			email: "",
			password: "",
			personaContacto: "",
			telefonoContacto: "",
			terminosCondiciones: false as true,
		} satisfies FormType,
	});
	const onSubmit: SubmitHandler<FormType> = async (data) => {
		const response = await mutation.mutateAsync({
			body: {
				username: data.email,
				password: data.password,

				razonSocial: data.razonSocial,
				personaContacto: data.personaContacto,
				telefonoContacto: data.telefonoContacto,
			},
		});

		if (response.code === "OK") {
			void navigate("/login");
			toast(
				"Registro exitoso. Aún es necesario que un administrador acepte el registro para usar la herramienta.",
				{ type: "success" },
			);
		} else {
			console.error("Error al registrar:", response);
			toast("Error al registrar. Contacta con el administrador.", {
				type: "error",
			});
		}

		void navigate("/login");
	};

	return (
		<Form {...form}>
			<form
				onSubmit={() => {
					void form.handleSubmit(onSubmit)();
				}}
				className="space-y-8"
			>
				<TextControl
					control={form.control}
					name="razonSocial"
					label="Razón Social"
					autoComplete="organization-name"
					placeholder="Razón Social"
					type="text"
				/>

				<TextControl
					control={form.control}
					name="email"
					label="Correo Electrónico"
					autoComplete="email"
					placeholder="Correo Electrónico"
					type="email"
				/>

				<TextControl
					control={form.control}
					name="password"
					label="Contraseña"
					autoComplete="new-password"
					placeholder="Contraseña"
					type="password"
				/>

				<TextControl
					control={form.control}
					name="personaContacto"
					label="Persona de contacto"
					autoComplete="name"
					placeholder="Nombre"
					type="text"
				/>

				<TextControl
					control={form.control}
					name="telefonoContacto"
					label="Número de contacto"
					autoComplete="tel"
					placeholder="Teléfono"
					type="tel"
				/>

				<CheckboxControl
					control={form.control}
					name="terminosCondiciones"
					label={
						<>
							<span>Acepto los terminos y condiciones de uso y</span>
							<br />
							<span>la politica de privacidad.</span>
						</>
					}
				/>

				<Button type="submit" className="float-right">
					Registrar
				</Button>
			</form>
		</Form>
	);
}

const FormSchema = z.object({
	razonSocial: z
		.string({
			error: "La razón social es obligatoria.",
		})
		.min(2, {
			message: "La razón social debe tener al menos 2 caracteres.",
		}),
	email: z
		.email({
			message: "El correo electrónico debe ser válido.",
		})
		.transform((email) => email.toLowerCase()),
	password: z
		.string({
			error: "La contraseña es obligatoria.",
		})
		.regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/, {
			message:
				"La contraseña debe tener al menos 8 caracteres, una letra mayúscula, una letra minúscula y un número.",
		}),
	personaContacto: z
		.string({
			error: "El nombre de contacto es obligatorio.",
		})
		.min(2, {
			message: "El nombre de contacto debe tener al menos 2 caracteres.",
		}),
	telefonoContacto: z
		.string({
			error: "El número de contacto es obligatorio.",
		})
		.regex(/^(\+\d{1,3})?(\d{6,12})$/, {
			message:
				"El número de contacto debe de ser un número de teléfono válido.",
		}),
	terminosCondiciones: z.literal(true, {
		error: "Debes aceptar los términos y condiciones.",
	}),
});
type FormType = z.infer<typeof FormSchema>;
